import { useState } from 'react';
import { NAV_ITEMS } from '@/_shared/constants/nav-items.constant';
import { useRouter } from 'next/router';
import Image from 'next/image';
import Breadcrumbs from './breadcrumbs.component';
import { ProfileMenu } from './profile-menu.component';
import { BusinessUnitSwitcher } from './business-unit-switcher.component';
import { NotificationPanel } from './notification-panel.component';
import Link from 'next/link';
import {
  Bell,
  FileSearch,
  Flag,
  Home,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { Button } from '@/components/buttons';

const Navbar = () => {
  const [sidebar, setSidebar] = useState(false);
  const [notiPanelOpen, setNotiPanelOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const { pathname, push } = useRouter();

  const active = 'bg-gray-100 hover:bg-gray-200';

  const handleNavigation = async (path: string, newTab?: boolean) => {
    if (newTab) {
      window.open(path, '_blank');
      return;
    }
    await push(path);
    setSidebar(false);
  };

  const toggleSidebar = (state: boolean) => {
    setSidebar(state);
  };

  return (
    <>
      <nav className="fixed z-40 flex w-[-webkit-fill-available] items-center border-b border-gray-border bg-white px-4 py-3 lg:h-16 lg:py-0">
        <div className="w-full flex justify-between items-center lg:flex-row-reverse flex-wrap">
          {/* Mobile menu button */}
          <button className="lg:hidden" onClick={() => toggleSidebar(true)}>
            <PanelLeftOpen className="text-lg size-4" />
          </button>
          <div className="text-lg flex gap-4 items-center">
            {/* <NotiPanel /> */}
            <button
              type="button"
              className="relative rounded-full p-2 hover:bg-gray-100"
              aria-label="Notifications"
              onClick={() => setNotiPanelOpen(true)}
            >
              <Bell className="size-5" />
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium leading-none text-white">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            <NotificationPanel
              open={notiPanelOpen}
              onClose={() => setNotiPanelOpen(false)}
              onUnreadCountChange={setUnreadCount}
            />
            <div className="border-l border-gray-border pl-3 flex items-center gap-3">
              <BusinessUnitSwitcher />
              <ProfileMenu />
            </div>
          </div>
          <hr className="my-4 -ml-4 -mr-8 w-screen border-gray-border lg:hidden" />
          <div className="flex gap-2 w-full lg:w-fit">
            <Breadcrumbs className="flex lg:min-h-15 lg:items-center bg-white" />
          </div>
        </div>
      </nav>
      {/* Sidebar */}
      <div className={`fixed inset-0 flex ${sidebar ? 'z-50' : '-z-10'}`}>
        <button
          className={`fixed inset-0 bg-black opacity-0 transition-opacity duration-300 ${
            sidebar ? 'opacity-50' : 'opacity-0'
          }`}
          onClick={() => toggleSidebar(false)}
        />
        <div
          className={`absolute lg:hidden flex h-full w-64 min-w-64 flex-col overflow-y-auto border-r border-r-gray-200 py-4 px-2 xl:p-4 transition-transform duration-300 transform ${
            sidebar ? 'translate-x-0' : '-translate-x-full'
          } bg-white z-50`}
        >
          <div
            className={`sticky top-0 z-10 flex h-10 shrink-0 justify-between bg-white px-2 mb-5 xl:p-0`}
          >
            <Image
              className="h-full w-[calc(100%-24px)]"
              src="/assets/MeterCube_by_Cube.svg"
              width={450}
              height={100}
              alt="MeterCube logo"
            />
            <button
              className={`${
                sidebar && 'flex justify-center items-center'
              } text-center transform transition-transform duration-75 hover:scale-110`}
              onClick={() => toggleSidebar(false)}
            >
              <PanelLeftClose />
            </button>
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-scroll">
            <button className={`flex items-center`}>
              <Link
                onClick={() => handleNavigation('/')}
                href="/hub"
                className={`h-10 flex items-center p-2 rounded-lg hover:bg-indigo-100 gap-2 w-full ${
                  pathname === '/hub' && active
                }`}
              >
                <span>
                  <Home />
                </span>
                <span className="text-sm">Hub</span>
              </Link>
            </button>
            {NAV_ITEMS.map((button) => (
              <>
                <button key={button.id} className={`flex items-center`}>
                  <Link
                    onClick={() => handleNavigation(button.route!)}
                    href={button.route ?? ''}
                    className={`h-10 flex items-center p-2 rounded-lg hover:bg-indigo-100 gap-2 w-full ${
                      pathname === button.route && active
                    }`}
                  >
                    <span>{button.icon}</span>
                    <span className="whitespace-nowrap text-sm">
                      {button.label}
                    </span>
                  </Link>
                </button>
                {button.child && (
                  <div className="ml-4 pl-3 border-l border-l-gray-300 gap-y-2 flex flex-col">
                    {button.child.map((sub) => (
                      <Link
                        key={sub.id}
                        onClick={() => handleNavigation(sub.route!)}
                        href={sub.route ?? ''}
                        className={`h-10 flex items-center p-2 rounded-lg hover:bg-indigo-100 gap-2 ${
                          pathname === sub.route && active
                        } ${'w-full'} `}
                      >
                        <span className="whitespace-nowrap text-sm">
                          {sub.label}
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
              </>
            ))}
            <div className="mt-auto flex flex-col gap-2 pt-4">
              <Button
                variant="filter"
                type="button"
                onClick={() => void handleNavigation('/report-issue')}
                className="flex h-11 w-full items-center justify-center gap-2 px-3"
                iconStart={<Flag className="size-4 shrink-0" />}
              >
                <span className="text-sm font-medium">Report Issue</span>
              </Button>
              <Button
                variant="outline-gray"
                type="button"
                onClick={() => void handleNavigation('/issue-tracker')}
                className="flex h-11 w-full items-center justify-center gap-2 px-3"
                iconStart={<FileSearch className="size-4 shrink-0" />}
              >
                <span className="text-sm font-medium">Issue Tracker</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
